import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { updateCertification } from '../../actions/certificationActions'
import { bulletsFromText, isPercentage, showGenericError } from '../../utility/certificationBuilder'
import { useForm } from './useForm'
import CertificationDetailsCard from './CertificationDetailsCard'
import CompletionCard from './CompletionCard'
import AccessCard from './AccessCard'
import PublishChecklist from './PublishChecklist'

import classes from './SettingsTab.module.scss'

const initialForm = (certification) => ({
  name: certification.name,
  level: String(certification.level),
  description: certification.description || '',
  bullets: certification.bullets.join('\n'),
  sequential: certification.sequential,
  accoladeId: certification.accolade?.id || null,
  passMark: String(certification.finalQuiz.passMark),
})

const ATTRIBUTES = {
  name: (form) => ({ name: form.name.trim() }),
  level: (form) => ({ level: Number(form.level) }),
  description: (form) => ({ description: form.description.trim() }),
  bullets: (form) => ({ bullets: bulletsFromText(form.bullets) }),
  sequential: (form) => ({ sequential: form.sequential }),
  accoladeId: (form) => ({ accoladeId: form.accoladeId }),
  passMark: (form, certification) => ({ finalQuizAttributes: { id: certification.finalQuiz.id, passMark: Number(form.passMark) } }),
}

const ERRORS = {
  name: (form) => form.name.trim() === '' && 'certificationBuilder.details.errors.name',
  passMark: (form) => !isPercentage(form.passMark) && 'certificationBuilder.quiz.passMarkError',
}

const API_ERROR_KEYS = { passMark: 'finalQuiz.passMark' }

const SettingsTab = ({ certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { form, setForm, errors, setErrors, setField } = useForm(() => initialForm(certification))

  const saveField = (field, nextForm = form) => {
    const errorKey = ERRORS[field]?.(nextForm)
    if (errorKey) { setErrors({ [field]: t(errorKey) }); return }

    const attributes = ATTRIBUTES[field](nextForm, certification)
    if (JSON.stringify(attributes) === JSON.stringify(ATTRIBUTES[field](initialForm(certification), certification))) { return }

    dispatch(updateCertification({ id: certification.id, ...attributes })).unwrap()
      .catch((apiErrors) => apiErrors?.[API_ERROR_KEYS[field] || field]
        ? setErrors({ [field]: apiErrors[API_ERROR_KEYS[field] || field][0] })
        : showGenericError())
  }

  const selectField = (field, value) => {
    const nextForm = { ...form, [field]: value }

    setForm(nextForm)
    saveField(field, nextForm)
  }

  return (
    <div className={classes.layout}>
      <div className='d-flex flex-column'>
        <CertificationDetailsCard certification={certification} form={form} errors={errors} onChange={setField} onBlur={saveField} />
        <CompletionCard form={form} errors={errors} onChange={setField} onBlur={saveField} onSelect={selectField} />
        <AccessCard certification={certification} />
      </div>
      <PublishChecklist certification={certification} />
    </div>
  )
}

SettingsTab.propTypes = {
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    level: PropTypes.number.isRequired,
    description: PropTypes.string,
    bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
    sequential: PropTypes.bool.isRequired,
    accolade: PropTypes.shape({ id: PropTypes.number.isRequired }),
    finalQuiz: PropTypes.shape({
      id: PropTypes.number.isRequired,
      passMark: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
}

export default SettingsTab
